import React, { useState } from "react";
import { useHistory } from "react-router";
import clsx from "clsx";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  MenuItem,
  Menu,
  IconButton,
  Box,
  Divider,
  Avatar,
} from "@material-ui/core";
import {
  CustomBadge,
  CustomCheckbox,
  CustomCopyToClipboard,
  CustomIconButton,
  LeadTypeBadges,
  SocialLinks,
} from "../../../../../Components";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import { AfterSalePermissions } from "../../../../../Permissions";
import { BulkAssignAfterSales } from "../../../../../Services";
import {
  getDownloadableLink,
  havePermissionToViewDetails,
  returnPropsByPermissions,
  truncateString,
} from "../../../../../Helper";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

// Icons
import {
  ArrowUpRight,
  MoreVertIcon,
  ActivityIcon,
  Sale02Icon,
  PlusIcon,
  User03Icon,
} from "../../../../../assets/icons";

// Styles
import useStyles from "./styles";
import { PersonAdd } from "@material-ui/icons";

function AfterSalesClientCard({
  isChecked,
  handleCardCheck,
  item,
  cardActions,
  hasCheckedCards,
  refetch,
}) {
  const styles = useStyles();
  const { translate } = useTranslate("AfterSalesView");
  const { isDarkMode } = useSelector((state) => state.theme);
  const navigate = useHistory();
  const contactDetails = JSON.parse(item?.contactJson || "{}");
  const {
    theme: { palette },
  } = useSelectedTheme();

  const {
    loginResponse: { userId },
  } = useSelector((state) => state.login);
  const [anchorEl, setAnchorEl] = useState(null);
  const { setAlertBoxContent } = useVerticalNav();

  const hasAssignedAgent = item?.afterSalesAssigneeName;

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl({
      top: rect.top + 25,
      left: rect.left + 15,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBulkAssign = async (params) => {
    try {
      const res = await BulkAssignAfterSales(params);
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: params.assigneeId ? translate("Owner assigned Successfully") : translate("Owner Cleared Successfully"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        if (refetch) refetch();
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title:  params.assigneeId ? translate("Error assigning Owner") : translate("Error Clearing Owner"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  };

  const loginRes = useSelector((state) => state?.login?.loginResponse);
  const allowedAssignToMe = !!loginRes?.roles?.find(role => role.rolesId === "aa7f4575-ef1b-4119-8fae-7ad4d9dec43a");
  
  let options = [
    {
      label: translate("link-to-offer"),
      icon: (
        <Sale02Icon
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () => {
        cardActions.linkOffer(item);
      },
      permissionId: AfterSalePermissions.LinkOffer.permissionsId,
    },
    {
      label: translate("add-activity"),
      icon: (
        <ActivityIcon
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () => cardActions.createNewActivity(item),
      permissionId: AfterSalePermissions.AddActivity.permissionsId,
    },
    ...(allowedAssignToMe ? [{
      label: translate("Assign to Me"),
      icon: <PersonAdd width="16" height="16" color="inherit" />,
      permissionId: AfterSalePermissions.ViewAfterSale.permissionsId,
      handleOnclick: () =>
        handleBulkAssign({ contactIds: [item?.contactId], newUserId: userId }),
    }] : []),
    {
      label: translate("create-lead"),
      icon: (
        <PlusIcon width="16" height="16" fill={palette.foreground.quarterary} />
      ),
      handleOnclick: () => cardActions.createNewLead(item),
      permissionId: AfterSalePermissions.CreateLead.permissionsId,
    },
    {
      label: translate("clear-assigned-agent"),
      icon: (
        <User03Icon
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () =>
        handleBulkAssign({ contactIds: [item?.contactId], assigneeId: null }),
      permissionId: AfterSalePermissions.AssignAfterSalesOwner.permissionsId,
    },
  ];

  const goToProfile = () => {
    const params = new URLSearchParams({
      id: item?.contactId,
    });
    navigate.push(`/home/after-sales/client-profile?${params}`);
  };

  if (hasAssignedAgent) {
    options = options.filter((option) => option.label !== "Assign to Me");
  }

  return (
    <Box
      key={item?.contactId || item?.id}
      className={clsx(styles.ContactCardWrapper, {
        // [styles.active]: item?.id === activeItem?.id,
      })}
      onClick={() => {}}
    >
      <Box className={styles.cardHeader}>
        <Box
          style={{
            width: "28px",
          }}
          className={clsx(styles.checkBoxContainer, {
            [styles.activeCard]: hasCheckedCards,
          })}
        >
          <CustomCheckbox
            checked={isChecked}
            onChange={() => handleCardCheck(item?.contactId || item?.id)}
          />
        </Box>
        <Box className={styles.flex}>
          <Box
            className={styles.avatarWrapper}
            style={isDarkMode ? { backgroundColor: "unset" } : {}}
          >
            {
              <Avatar
                className={
                  item.contactImage ? styles.avatar : styles.defaultAvatar
                }
                src={
                  item?.contactImage
                    ? getDownloadableLink(item?.contactImage?.fileId)
                    : item?.contactsTypeId === 2 //"CORPORATE"
                    ? "/images/contacts/CorporateAvatar.svg"
                    : "/images/contacts/MaleAvatar.svg"
                }
              />
            }
          </Box>
        </Box>

        <Box
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IconButton
            aria-controls="more-button"
            aria-haspopup="true"
            aria-label="more-button"
            id="more-button"
            disableRipple
            onClick={handleClick}
            className={styles.menuIcon}
            disabled={
              !options.filter(({ permissionId }) =>
                havePermissionToViewDetails([{ permissionsId: permissionId }])
              )
            }
          >
            <MoreVertIcon
              width="20"
              height="20"
              fill={
                anchorEl
                  ? palette.foreground.quarterary
                  : palette.foreground.quinary
              }
            />
          </IconButton>
          <Menu
            id="more-button"
            PaperProps={{ elevation: 0 }}
            MenuListProps={{ className: styles.customMenuList }}
            anchorReference="anchorPosition"
            anchorPosition={
              anchorEl ? { top: anchorEl.top, left: anchorEl.left } : undefined
            }
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {options
              .filter(({ permissionId }) =>
                havePermissionToViewDetails([{ permissionsId: permissionId }])
              )
              .map(({ label, icon, handleOnclick }) => {
                return (
                  <MenuItem
                    disableRipple
                    key={label}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOnclick();
                      handleClose();
                    }}
                    className={styles.option}
                  >
                    {icon}
                    <span>{label}</span>
                  </MenuItem>
                );
              })}
          </Menu>
        </Box>
      </Box>

      <Box className={styles.detailsWrapper}>
        <Box className={styles.details}>
          <label>
            {item.contactsTypeId === 2
              ? translate("corporate")
              : translate("individual")}
          </label>

          {item?.clientClass === "VIP" && (
            <CustomBadge
              Style={{
                padding: "0 2px",
                borderRadius: "2px",
                lineHeight: "20px",
              }}
              label={translate("vip")}
              BackgroundColor={palette.utility.brand_300}
              BorderColor={palette.utility.brand_300}
              Color={"#FFF"}
            />
          )}

          {item?.clientClass === "VVIP" && (
            <CustomBadge
              Style={{
                padding: "0 2px",
                borderRadius: "2px",
                lineHeight: "20px",
              }}
              label={translate("vvip")}
              BackgroundColor={palette.utility.brand_600}
              BorderColor={palette.utility.brand_600}
              Color={"#FFF"}
            />
          )}
        </Box>
        <Box className={styles.nameContainer} onClick={() => {}}>
          <label>{truncateString(item?.contactName || "", 45)}</label>
          <CustomIconButton
            variant="text"
            size="xs"
            boxShadow="none"
            color="secondary"
            onClick={goToProfile}
          >
            <ArrowUpRight
              width="20"
              height="20"
              fill={palette.button.secondaryGray_fg}
            />
          </CustomIconButton>
        </Box>
        <Box className={styles.idSection}>
          <CustomCopyToClipboard
            data={item.contactId || item?.id}
            childrenData={"#" + (item.contactId || item?.id)}
            copyDoneShowTime={1000}
            fontSize="14px"
          />
        </Box>
      </Box>
      <SocialLinks
        activeItem={contactDetails?.contact}
        contactId={item?.contactId}
        isAfterSale
      />
      <Box style={{ height: "24px" }}>
        {item?.leadTypes && <LeadTypeBadges leadTypesList={item.leadTypes} />}
      </Box>
      <Box>
        <Box className={styles.userInfo}>
          <span>
            {item?.dateOfBirth && item?.contactsTypeId === 1 && (
              <>
                DOB: {moment(item.dateOfBirth).format("DD/MM/YYYY")}
                <span className={styles.hyphen}>-</span>
              </>
            )}
            <CustomBadge
              Style={{ padding: "0 6px", height: "22px", margin: "0 6px" }}
              label={item?.numberOfBuyerSaleTransactions ?? 0}
              SizeVariant={"small"}
              BackgroundColor={palette.utility.utility_gray_50}
              BorderColor={palette.utility.utility_gray_200}
              Color={palette.utility.utility_gray_700}
            />
            <span>{translate("sale-units")}</span>
          </span>

          <label>
            {(() => {
              const language = item?.language ? item.language : "";
              const nationality = contactDetails?.contact?.nationality
                ?.lookupItemName
                ? contactDetails.contact.nationality.lookupItemName
                : "";
              if (language && nationality) return `${language}, ${nationality}`;
              if (language) return language;
              if (nationality) return nationality;
              return "";
            })()}
          </label>
        </Box>

        <Divider className={styles.divider} />

        <Box className={styles.dateContainer}>
          <label>{translate("created-on")}</label>

          <Box className={styles.dateText}>
            <label>{moment(item?.createdOn).format("DD MMM YYYY")}</label>
            <span>{truncateString(item?.createdBy)}</span>
          </Box>
        </Box>
        {item?.afterSalesAssigneeName &&
          item?.afterSalesAssigneeName !== "" &&
          item?.afterSalesAssigneeName !== null && (
            <Box className={styles.assignedAgent}>
              <label>{translate("assigned-agent")}</label>
              <Box className={styles.assignedAgentText}>
                <label>{item?.afterSalesAssigneeName}</label>
              </Box>
            </Box>
          )}
      </Box>
    </Box>
  );
}

export default AfterSalesClientCard;
